/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SystemModule
 */

import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Type,
} from '@angular/core';

import {
    IPortalConfiguration,
    ISecureChannelConfiguration,
} from 'generated-types';

import {
    PortalConfiguration,
    SecureChannelConfiguration,
    SystemConfiguration,
} from 'object-types';

import { L10nService } from '@vmw/ngx-vip';

import {
    SSLProfileCollection,
} from 'ajs/modules/security/factories/ssl-profile/ssl-profile.collection.factory';

import {
    CertificateCollection,
} from 'ajs/modules/security/factories/certificates/certificate.collection.factory';

import './system-settings-system-access-config.component.less';

import { SystemConfig } from 'ajs/modules/system';

import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import * as globalL10n from 'global-l10n';

import {
    TrustedHostProfileCollection,
    TTrustedHostProfileCollection,
} from 'ng/modules/collections/factories';

import {
    PKIProfileCollection,
    TPkiProfileCollection,
} from 'ajs/modules/security/factories/pki-profile/pki-profile.collection.factory';

import { DialogService } from 'ng/modules/core/services';
import { AviContinueConfirmationComponent } from 'ng/modules/dialog';
import * as l10n from './system-settings-system-access-config.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const { ...globalL10nKeys } = globalL10n;

/**
 * Enable Trusted Host Profile confirmation dialog.
 */
const TRUSTED_HOST_PROFILE_CONFIRMATION_DIALOG = 'TRUSTED_HOST_PROFILE_CONFIRMATION_DIALOG';

type TSSLProfileCollection = typeof SSLProfileCollection;
type TCertificateCollection = typeof CertificateCollection;

/**
 * @description Component for System Access Config System Settings Modal.
 * @author Kondiparthi Shanmukha Sarath, Shahab Hashmi
 */
@Component({
    selector: 'system-settings-system-access-config',
    templateUrl: './system-settings-system-access-config.component.html',
})
export class SystemSettingsSystemAccessConfigComponent implements OnInit, OnDestroy {
    /**
     * Portal configuration of the controller.
     */
    @Input()
    public portalConfig: IPortalConfiguration;

    /**
     * Secure channel configuration.
     */
    @Input()
    public secureChannelConfig: ISecureChannelConfiguration;

    @Input()
    public systemConfig: SystemConfig;

    /**
     * Allowed ciphers in system configuration.
     */
    @Input()
    public allowedSshCiphers: string[];

    /**
     * Allowed hmacs in system configuration.
     */
    @Input()
    public allowedSshHmacs: string[];

    public trustedHostProfileGridConfig: IAviDataGridConfig;

    /**
     * SSL Profile collection.
     */
    public sslProfileCollection: SSLProfileCollection;

    /**
     * SSL/TLS Certificates collection.
     */
    public certificateCollection: CertificateCollection;

    /**
     * A boolean flag to restrict SSL Keys to a limit of 5.
     */
    public isSslKeyCertsReadonly: boolean;

    /**
     * A boolean flag to restrict Secure Channel SSL Keys to a limit of 1.
     */
    public isSecureChannelCertListReadonly: boolean;

    /**
     * Object type for template usage.
     */
    public readonly objectTypes = {
        PortalConfiguration,
        SecureChannelConfiguration,
        SystemConfiguration,
    };

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Trusted Host Profile collection.
     */
    public trustedHostProfileCollection: TrustedHostProfileCollection;

    public readonly pkiProfileCollection: PKIProfileCollection;

    constructor(
        private readonly l10nService: L10nService,
        private readonly dialogService: DialogService,
        @Inject(SSLProfileCollection)
        SSLProfileCollection: TSSLProfileCollection,
        @Inject(CertificateCollection)
        CertificateCollection: TCertificateCollection,
        @Inject(TrustedHostProfileCollection)
        TrustedHostProfileCollection: TTrustedHostProfileCollection,
        @Inject(PKIProfileCollection)
        PKIProfileCollection: TPkiProfileCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.sslProfileCollection = new SSLProfileCollection();
        this.certificateCollection = new CertificateCollection();
        this.trustedHostProfileCollection = new TrustedHostProfileCollection();
        this.pkiProfileCollection = new PKIProfileCollection();
    }

    /** @override */
    public ngOnInit(): void {
        this.setIsSslKeyCertsReadonlyFlag();
        this.setIsSecureChannelCertListReadonlyFlag();
    }

    /** @override */
    public ngOnDestroy(): void {
        this.sslProfileCollection.destroy();
        this.certificateCollection.destroy();
        this.trustedHostProfileCollection.destroy();
    }

    /**
     * Sets isSslKeyCertsReadonly boolean flag on model change.
     */
    public setIsSslKeyCertsReadonlyFlag(): void {
        this.isSslKeyCertsReadonly = this.portalConfig.sslkeyandcertificate_refs?.length >= 5;
    }

    /**
     * Sets isSecureChannelCertListReadonly boolean flag on model change.
     */
    public setIsSecureChannelCertListReadonlyFlag(): void {
        this.isSecureChannelCertListReadonly = Boolean(
            this.secureChannelConfig.sslkeyandcertificate_refs?.length,
        );
    }

    /**
     * Open confirmation dialog on checkbox toggle.
     */
    public confirmHostHeaderProtectionToggle(): void {
        const {
            dialogService,
            l10nService,
            l10nKeys,
        } = this;

        dialogService.add({
            id: TRUSTED_HOST_PROFILE_CONFIRMATION_DIALOG,
            component: AviContinueConfirmationComponent as Type<Component>,
            componentProps: {
                warning: l10nService.getMessage(l10nKeys.trustedHostProfileWarningMessage),
                customHeader: l10nService.getMessage(l10nKeys.warningLabel),
                onConfirm: () => {
                    if (this.systemConfig.config.trusted_host_profiles_refs?.length) {
                        delete this.systemConfig.config.trusted_host_profiles_refs;
                    }

                    dialogService.remove(TRUSTED_HOST_PROFILE_CONFIRMATION_DIALOG);
                },
                onClose: () => {
                    this.systemConfig.config.enable_host_header_check =
                        !this.systemConfig.config.enable_host_header_check;
                    dialogService.remove(TRUSTED_HOST_PROFILE_CONFIRMATION_DIALOG);
                },
            },
        });
    }
}
